import React, {Fragment, ReactNode} from 'react';

import jquery from 'jquery';

import {ClickToEditButton} from 'app/components/general/ClickToEditButton';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';
import {
  ActionsButton,
  ActionsMenuItemsType,
} from 'app/general/widgets/ActionsButton';
import {ImagesCarousel} from 'app/general/widgets/ImagesCarousel';
import {OrphanStepBadge} from 'app/procedures/components/OrphanStepBadge';
import {ProcedureStepFiles} from 'app/procedures/components/ProcedureStepFiles';
import {StepViewFieldWrapper} from 'app/procedures/components/step-form-fields/StepViewFieldWrapper';
import {StyledStepContentSeparatorDiv} from 'app/procedures/components/structures';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {NextStepLinkItem} from 'app/steps/components/NextStepLinkItem';
import {StepLaneItem} from 'app/steps/components/StepLaneItem';
import {COLLAPSE_DURATION_MS} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {isRightToLeftWrapper} from 'app/utils/rtl';

import {ProcedureStepStructure} from '../ProcedureStepStructure';
import {ViewableAutomation} from '../ViewableAutomation';

import type {ProcedureStepWithDiffType} from 'app/tasks/types';
import type {AnyStepType} from 'sweet-types';

interface Props {
  step: ProcedureStepWithDiffType | AnyStepType;
  orphan?: boolean;
  steps: Array<AnyStepType>;
  setStepEditing: (
    id: string,
    editing: boolean,
    clearAll?: boolean,
    cb?: () => void
  ) => void;
  collapsed: boolean;
  onToggleReorderMode: () => void;
  onToggleCollapse: (id: string, collapse: boolean) => unknown;
  onRemoveStep: (id: string) => void;
  onCloneStep?: (id: string) => void;
  onConvertToEnding?: (id: string) => void;
  onConvertToNormal?: (id: string) => void;
  onConvertToDecision?: (id: string) => void;
  stepLaneId?: string | null;
  noBottomBorder?: boolean;
  AIEditButton?: ReactNode | null;
}

export function EditableProcedureStep({
  step,
  orphan = false,
  steps,
  setStepEditing,
  collapsed,
  onToggleReorderMode,
  onToggleCollapse,
  onRemoveStep,
  onConvertToEnding,
  onConvertToNormal,
  onConvertToDecision,
  stepLaneId = null,
  noBottomBorder,
  onCloneStep,
  AIEditButton,
}: Props): JSX.Element {
  const isMountingRef = React.useRef(true);
  const collapsableDivRef = React.useRef<HTMLDivElement | null>(null);

  function toggleCollapse(collapse: boolean, disableAnimation = false) {
    const command = collapse ? 'slideUp' : 'slideDown';
    if (collapsableDivRef.current != null) {
      jquery(collapsableDivRef.current)[command](
        disableAnimation ? 0 : COLLAPSE_DURATION_MS
      );
    }
  }

  React.useEffect(() => {
    if (isMountingRef.current) {
      isMountingRef.current = false;
      if (collapsed) {
        toggleCollapse(true, true);
      }
    } else {
      toggleCollapse(collapsed);
    }
  }, [collapsed]);

  function stopImagesAndFilesOnClickPropagation(
    e: React.SyntheticEvent<HTMLElement>
  ) {
    // if we don't stop the propagation here the edit-step interface
    // will be opened when user try to navigate through the step
    // images/videos/files
    e.stopPropagation();
  }

  function getStepActions(): ActionsMenuItemsType {
    let actions: ActionsMenuItemsType = [
      {
        text: gt('Move Steps'),
        icon: 'fa fa-fw fa-sort',
        key: 'move-steps',
        disabled: false,
        callback: () => onToggleReorderMode(),
      },
    ];

    // hide collapse/expand controls in processprocedure steps
    if (step.type !== 'processprocedure') {
      if (collapsed) {
        actions.push({
          text: gt('Expand Step'),
          icon: 'fa fa-chevron-down',
          key: 'expand-step',
          callback: () => onToggleCollapse(step.id, !collapsed),
        });
      } else {
        actions.push({
          text: gt('Collapse Step'),
          icon: 'fa fa-chevron-up',
          key: 'collapse-step',
          callback: () => onToggleCollapse(step.id, !collapsed),
        });
      }
    }

    if (step.type !== 'ending' && onConvertToEnding) {
      actions.push({
        text: gt('Convert to an ending'),
        icon: 'far fa-circle',
        key: 'ending-step',
        disabled: false,
        callback: () => onConvertToEnding(step.id),
      });
    }

    if (step.type !== 'content' && onConvertToNormal) {
      actions.push({
        text: gt('Convert to a normal step'),
        icon: 'fas fa-dot-circle',
        key: 'normal-step',
        disabled: false,
        callback: () => onConvertToNormal(step.id),
      });
    }

    if (step.type !== 'decision' && onConvertToDecision) {
      actions.push({
        text: gt('Convert to a decision'),
        icon: 'far decision-svg',
        key: 'decision-step',
        disabled: false,
        callback: () => onConvertToDecision(step.id),
      });
    }

    if (onCloneStep) {
      actions.push({
        text: gt('Duplicate'),
        icon: 'fa fa-fw fa-copy',
        key: 'duplicate-step',
        disabled: false,
        callback: () => onCloneStep(step.id),
      });
    }

    actions.push({
      text: gt('Delete Step'),
      icon: 'fa fa-times',
      key: 'delete-step',
      disabled: false,
      callback: () => onRemoveStep(step.id),
    });
    return actions;
  }

  const stepTitle = ('title' in step ? step.title : '') ?? '';
  const stepContent = ('content' in step ? step.content : '') ?? '';
  const images = ('images' in step && step.images) || [];
  const embeds = ('embeds' in step && step.embeds) || [];

  const carousel = [...images, ...embeds];

  return (
    <ProcedureStepStructure
      style={{
        cursor: 'pointer',
      }}
      className={
        isRightToLeftWrapper(stepTitle) || isRightToLeftWrapper(stepContent)
          ? 'right-to-left-text'
          : ''
      }
      type={step.type}
      changedState={'changedState' in step ? step.changedState : undefined}
      n={step.n}
      beforeStepContent={<StepLaneItem stepLaneId={stepLaneId} />}
      beforeTitleContent={
        <Fragment>
          {orphan === true ? <OrphanStepBadge type={step.type} /> : null}
          {onToggleReorderMode != null &&
          onToggleCollapse != null &&
          onRemoveStep != null ? (
            <div className="d-flex float-right">
              <ActionsButton
                stopClickPropagation={true}
                actions={getStepActions()}
              />
            </div>
          ) : null}
        </Fragment>
      }
      title={
        <Fragment>
          {stepTitle !== '' ? (
            stepTitle
          ) : (
            <i className="unnamed">
              {step.type === 'decision'
                ? gt('Give this decision a title')
                : step.type === 'processprocedure'
                ? step.deleted
                  ? gt('Deleted Procedure')
                  : gt('Untitled Procedure')
                : gt('Give this step a title.')}
            </i>
          )}
          <ClickToEditButton />
          {AIEditButton}
        </Fragment>
      }
      onClick={() => setStepEditing(step.id, true, true)}
      afterTitleContent={
        <div className="collapsable-area" ref={collapsableDivRef}>
          <StyledStepContentSeparatorDiv>
            <UserContentContainer
              innerRef={collapsableDivRef}
              content={stepContent}
            />
          </StyledStepContentSeparatorDiv>
          <div onClick={stopImagesAndFilesOnClickPropagation}>
            {carousel.length > 0 ? (
              <div className="images">
                <ImagesCarousel images={carousel} className="step-images" />
              </div>
            ) : null}

            {'files' in step && step.files.length ? (
              <ProcedureStepFiles files={step.files} />
            ) : null}
          </div>
          <StepViewFieldWrapper
            fields={'fields' in step ? step.fields : undefined}
            showNotification={() => undefined}
          />
          {'choices' in step ? (
            <ViewableChoices
              choices={step.choices}
              steps={steps}
              edit={true}
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
          {'automation' in step ? <ViewableAutomation step={step} /> : null}
          <NextStepLinkItem step={step} steps={steps} />
        </div>
      }
      noBottomBorder={noBottomBorder}
    />
  );
}
